import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, ChevronRight, AlertTriangle } from 'lucide-react';
import type { MatchSummary } from '../lib/api';
import { divisionLetter } from '../lib/groupMatchesByStage';
import { StatusBadge } from './StatusBadge';
import { HelpText } from './HelpText';
import { ResultEntryForm } from './ResultEntryForm';

// ─────────────────────────────────────────────────────────────────────────────
// MatchRow — one match's summary line + expandable inline ResultEntryForm.
// Extracted from AdminPanel.tsx's MatchesSection (its original home, R1
// comp-lifecycle-pipeline) so the Matches-step MatchScheduleBoard
// (TournamentSetup.tsx) can reuse the exact same row — clans, status badge,
// winner, scheduledAt, expand-to-score — instead of duplicating it.
// AdminPanel.tsx's MatchesSection imports this same component; behavior
// there is unchanged (byte-for-byte the same markup it used to define
// locally), plus one small addition both callers benefit from: a
// scheduledAt readout when the match carries one.
// ─────────────────────────────────────────────────────────────────────────────

export function MatchRow({ m, onResultSaved }: { m: MatchSummary; onResultSaved: () => void }) {
  const [expanded, setExpanded] = useState(false);
  // Confirmed matches stay editable — admins must be able to correct a result
  // (e.g. an OCR misread). The route allows overwrite; the UI must not hide it.
  const alreadyScored = m.status === 'CONFIRMED';
  // Clan display-name preservation (Lisa, 2026-07-13): prefer the match
  // row's denormalized clan1DisplayName/clan2DisplayName over the bare
  // clan1/clan2 identity (m.clan1/m.clan2 stay canonical — legacy rows
  // without the stamp fall back to them unchanged, same label either way).
  const clan1Label = m.clan1DisplayName ?? m.clan1;
  const clan2Label = m.clan2DisplayName ?? m.clan2;
  const winnerLabel = m.nightWinner === m.clan1 ? clan1Label : m.nightWinner === m.clan2 ? clan2Label : m.nightWinner;

  return (
    <div className="overflow-hidden rounded-lg bg-white/[0.04]">
      <div
        className={`flex flex-col gap-2 px-4 py-3 transition-colors sm:flex-row sm:items-center sm:justify-between sm:gap-3 ${
          m.status === 'DISPUTED' ? 'bg-red-500/10' : ''
        }`}
      >
        {/* Matchup — its own line on mobile so team names get the FULL row
            width instead of fighting the date/badge/action cluster below for
            space (that fight is what crushed names to one character and let
            the scheduledAt digits visually bleed over the status pill). Each
            name gets flex-1 + a min-width floor on mobile only — sm:+ reverts
            to the original natural-width + truncate behavior, byte-for-byte. */}
        <Link
          to={`/match/${encodeURIComponent(m.sk)}?compId=${encodeURIComponent(m.pk)}`}
          className="flex min-w-0 items-center gap-3 hover:opacity-80 sm:flex-1"
        >
          {m.status === 'DISPUTED' && <AlertTriangle className="h-4 w-4 shrink-0 text-red-400" />}
          {/* Division tag — see Matches.tsx's MatchLinkRow for why: week-
              grouping (groupMatchesByStage.ts) merges every division's
              fixtures into one "Week N" block, so this is what keeps a
              match's own division readable at a glance here too. */}
          {typeof m.groupIndex === 'number' && (
            <span className="shrink-0 font-mono text-[9px] font-bold uppercase tracking-widest text-ink-meta">
              Div {divisionLetter(m.groupIndex)}
            </span>
          )}
          <span className="min-w-[60px] flex-1 truncate text-sm font-medium text-white sm:min-w-0 sm:flex-none">{clan1Label}</span>
          <span className="shrink-0 text-xs text-ink-meta">vs</span>
          <span className="min-w-[60px] flex-1 truncate text-sm font-medium text-white sm:min-w-0 sm:flex-none">{clan2Label}</span>
          {m.scheduledAt && (
            <span className="hidden shrink-0 text-xs text-ink-meta sm:inline">
              {new Date(m.scheduledAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
        </Link>
        {/* Score/winner + status pill + action — second line on mobile, own
            non-overlapping container; flex-wrap is a safety net if it still
            doesn't all fit on one line on a very narrow phone. */}
        <div className="flex flex-wrap shrink-0 items-center gap-2">
          {m.nightWinner && <span className="text-xs text-ink-secondary">🏆 {winnerLabel}</span>}
          <StatusBadge status={m.status} />
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-accent-500 transition-colors hover:bg-white/[0.06]"
          >
            <Pencil className="h-3 w-3" /> {expanded ? 'Cancel' : alreadyScored ? 'Edit result' : 'Enter result'}
          </button>
          <ChevronRight className="h-4 w-4 text-ink-meta" />
        </div>
      </div>
      {expanded && (
        <div className="border-t border-white/[0.06] px-4 py-3">
          <HelpText>
            <p className="mb-2 text-xs text-ink-meta">Enter a match's result manually here — useful when OCR misses a screenshot. Edit a confirmed result to correct a misread; saving overwrites it immediately.</p>
          </HelpText>
          <ResultEntryForm
            matchId={m.sk}
            compId={m.pk}
            clan1={clan1Label}
            clan2={clan2Label}
            resolvedSeries={m.resolvedSeries}
            games={m.games}
            onSaved={() => { setExpanded(false); onResultSaved(); }}
            onCancel={() => setExpanded(false)}
          />
        </div>
      )}
    </div>
  );
}
